import { parseDate } from "@internationalized/date";
import { useDebouncedValue } from "@packages/core-client/hooks/useDebouncedValue.ts";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Selection } from "react-aria-components";
import type { DateRange } from "react-aria-components/DateRangePicker";
import { useTranslation } from "react-i18next";
import { type PeriodSelection, type ReviewHistoryFilter, ReviewHistoryFilters } from "#shared/periodSelection.ts";
import { Button } from "@/components/ui/button.tsx";
import { DateRangePicker, DateRangePickerTrigger } from "@/components/ui/date-range-picker.tsx";
import { FieldError } from "@/components/ui/field.tsx";
import { ListBox, ListBoxItem } from "@/components/ui/list-box.tsx";
import { Radio, RadioField, RadioGroup } from "@/components/ui/radio.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { usePeriodLabel } from "../hooks/usePeriodLabel.ts";
import { FilterPanelBody } from "@domains/reviews/client/components/reviewee-filters/FilterPanelBody.tsx";
import { FilterPanelHeader } from "@domains/reviews/client/components/reviewee-filters/FilterPanelHeader.tsx";
import { i18NextInstanceKey } from "../i18n/i18next.ts";
import { describePeriod } from "./comparison/comparisonSeries.ts";

const CUSTOM_RANGE_KEY = "custom";

const RANGE_SETTLE_MS = 400;

const NO_PRESETS: ReviewHistoryFilter[] = [];
const WHOLE_HISTORY: PeriodSelection = {};

export interface TimeRangeFilterPanelProps {
    selectionMode?: "single" | "multiple";
    period?: PeriodSelection;
    onChange?: (period: PeriodSelection) => void;
    selectedPresets?: ReviewHistoryFilter[];
    onSelectionChange?: (presets: ReviewHistoryFilter[]) => void;
    onBack?: () => void;
    maxSelections?: number;
}

interface TimeRangeBreakdownListProps {
    selectedPresets: ReviewHistoryFilter[];
    onSelectionChange: (presets: ReviewHistoryFilter[]) => void;
    onBack?: () => void;
    maxSelections?: number;
}

interface TimeRangeSinglePanelProps {
    period: PeriodSelection;
    onChange: (period: PeriodSelection) => void;
}

function toPresets(values: Iterable<string>) {
    return [...values].flatMap(value => ReviewHistoryFilters.filter(preset => preset === value));
}

function ignorePeriod() {}

function ignorePresets() {}

export function TimeRangeFilterPanel({ selectionMode = "single", period, onChange, selectedPresets, onSelectionChange, onBack, maxSelections }: TimeRangeFilterPanelProps) {
    if (selectionMode === "multiple") {
        return <TimeRangeBreakdownList selectedPresets={selectedPresets ?? NO_PRESETS} onSelectionChange={onSelectionChange ?? ignorePresets} onBack={onBack} maxSelections={maxSelections} />;
    }

    return <TimeRangeSinglePanel period={period ?? WHOLE_HISTORY} onChange={onChange ?? ignorePeriod} />;
}

function TimeRangeBreakdownList({ selectedPresets, onSelectionChange, onBack, maxSelections }: TimeRangeBreakdownListProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("TimeRangeFilterPanel", { i18n: i18nextInstance });
    const describePeriodLabel = usePeriodLabel();

    const isAtCap = maxSelections !== undefined && selectedPresets.length >= maxSelections;

    const handleSelectionChange = useCallback(
        (keys: Selection) => {
            const next = toPresets(keys === "all" ? ReviewHistoryFilters : [...keys].map(String));

            onSelectionChange(maxSelections === undefined ? next : next.slice(0, maxSelections));
        },
        [onSelectionChange, maxSelections]
    );

    const presets = useMemo(() => ReviewHistoryFilters.map(preset => ({ id: preset, label: describePeriodLabel({ kind: "preset", preset }) ?? preset })), [describePeriodLabel]);

    return (
        <FilterPanelBody className="w-72">
            <FilterPanelHeader title={t("title")} onBack={onBack} backLabel={t("back")} />
            <ListBox aria-label={t("title")} selectionMode="multiple" selectedKeys={selectedPresets} onSelectionChange={handleSelectionChange} escapeKeyBehavior="none" items={presets} className="min-h-0 border-0">
                {preset => (
                    <ListBoxItem id={preset.id} textValue={preset.label} isDisabled={isAtCap && !selectedPresets.includes(preset.id)}>
                        {preset.label}
                    </ListBoxItem>
                )}
            </ListBox>
        </FilterPanelBody>
    );
}

function TimeRangeSinglePanel({ period, onChange }: TimeRangeSinglePanelProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("TimeRangeFilterPanel", { i18n: i18nextInstance });
    const describePeriodLabel = usePeriodLabel();

    const descriptor = describePeriod(period);
    const hasRange = descriptor.kind === "range";

    const [isCustomPicked, setIsCustomPicked] = useState(hasRange);
    const isCustom = isCustomPicked || hasRange;
    const selectedMode = isCustom ? CUSTOM_RANGE_KEY : descriptor.kind === "preset" ? descriptor.preset : null;

    const storedRange = useMemo<DateRange | null>(() => {
        if (!hasRange || !period.from || !period.to) {
            return null;
        }

        try {
            return { start: parseDate(period.from), end: parseDate(period.to) };
        } catch {
            return null;
        }
    }, [hasRange, period.from, period.to]);

    const [draftRange, setDraftRange] = useState<DateRange | null>(null);
    const editedRange = draftRange ?? storedRange;
    const settledRange = useDebouncedValue(draftRange, RANGE_SETTLE_MS);

    const handleModeChange = useCallback(
        (value: string) => {
            setDraftRange(null);

            if (value === CUSTOM_RANGE_KEY) {
                setIsCustomPicked(true);
                onChange({});

                return;
            }

            const preset = ReviewHistoryFilters.find(candidate => candidate === value);

            setIsCustomPicked(false);
            onChange(preset ? { reviewHistoryFilter: preset } : {});
        },
        [onChange]
    );

    const handleRangeChange = useCallback((value: DateRange | null) => setDraftRange(value), []);

    useEffect(() => {
        if (!settledRange || settledRange !== draftRange) {
            return;
        }

        if (settledRange.end.compare(settledRange.start) < 0) {
            return;
        }

        const from = settledRange.start.toString();
        const to = settledRange.end.toString();

        if (from !== period.from || to !== period.to) {
            onChange({ from, to });
        }
    }, [settledRange, draftRange, period.from, period.to, onChange]);

    const validateRange = useCallback((value: DateRange | null) => (value && value.end.compare(value.start) < 0 ? t("invalidRange") : null), [t]);

    const handleClear = useCallback(() => {
        setDraftRange(null);
        setIsCustomPicked(false);
        onChange({});
    }, [onChange]);

    const isActive = isCustom || descriptor.kind === "preset";

    return (
        <div className="space-y-3">
            <RadioGroup aria-label={t("title")} value={selectedMode} onChange={handleModeChange}>
                {ReviewHistoryFilters.map(preset => (
                    <RadioField key={preset} value={preset}>
                        <Radio>{describePeriodLabel({ kind: "preset", preset })}</Radio>
                    </RadioField>
                ))}
                <div aria-hidden="true">
                    <Separator />
                </div>
                <RadioField value={CUSTOM_RANGE_KEY}>
                    <Radio>{t("customRange")}</Radio>
                </RadioField>
            </RadioGroup>

            {isCustom && (
                <DateRangePicker aria-label={t("rangeLabel")} value={editedRange} onChange={handleRangeChange} validate={validateRange} validationBehavior="aria">
                    <DateRangePickerTrigger />
                    <FieldError />
                </DateRangePicker>
            )}

            {isActive && (
                <Button intent="plain" size="xs" onPress={handleClear} className="text-muted-fg hover:text-fg">
                    {t("clear")}
                </Button>
            )}
        </div>
    );
}
